import React, { useEffect, useState } from "react";
import { X, AlertCircle } from "lucide-react";
import {
  useCreateAgeGroupMutation,
  useCreateLevelMutation,
  useCreateSubCategoryMutation,
  useUpdateAgeGroupMutation,
  useUpdateLevelMutation,
  useUpdateSubCategoryMutation,
} from "../../services/categories/categoriesApi";
import useClickOutsideModal from "../common/useClickOutsideModal";
import { showToast } from "../../utils/toast";
const AddCategoryOptionModal = ({
  isOpen,
  onClose,
  edit = false,
  item = null,
  type = "subCategory",
}) => {
  const [title, setTitle] = useState("");
  const [apiError, setApiError] = useState("");

  const [createSubCategory, { isLoading: isCreatingSubCategory }] =
    useCreateSubCategoryMutation();
  const [updateSubCategory, { isLoading: isUpdatingSubCategory }] =
    useUpdateSubCategoryMutation();

  const [createAgeGroup, { isLoading: isCreatingAgeGroup }] =
    useCreateAgeGroupMutation();

  const [updateAgeGroup, { isLoading: isUpdatingAgeGroup }] =
    useUpdateAgeGroupMutation();

  const [createCourseLevel, { isLoading: isCreatingCourseLevel }] =
    useCreateLevelMutation();

  const [updateCourseLevel, { isLoading: isUpdatingCourseLevel }] =
    useUpdateLevelMutation();

  const isSubmitting =
    isCreatingSubCategory ||
    isUpdatingSubCategory ||
    isCreatingAgeGroup ||
    isUpdatingAgeGroup ||
    isCreatingCourseLevel ||
    isUpdatingCourseLevel;

  const modalRef = useClickOutsideModal(onClose);
  const config = {
    subCategory: {
      label: "Sub Category",
      placeholder: "Enter sub category title",
    },
    ageGroup: {
      label: "Age Group",
      placeholder: "Enter age group",
    },
    courseLevel: {
      label: "Course Level",
      placeholder: "Enter course level",
    },
  };

  const currentConfig = config[type] || config.subCategory;
  useEffect(() => {
    if (edit && item) {
      setTitle(item.title || "");
    } else {
      setTitle("");
    }

    setApiError("");
  }, [edit, item, isOpen, type]);

  if (!isOpen) {
    return null;
  }

  const handleSubmit = async (event) => {
    event.preventDefault();

    const trimmedTitle = title.trim();

    if (!trimmedTitle) {
      setApiError(`${currentConfig.label} is required.`);
      return;
    }

    setApiError("");

    try {
      if (type === "subCategory") {
        const response = edit
          ? await updateSubCategory({
              id: item._id,
              title: trimmedTitle,
            }).unwrap()
          : await createSubCategory({
              title: trimmedTitle,
            }).unwrap();
        showToast({
          message: response?.message || "Operation successful",
          type: "success",
        });
      }

      if (type === "ageGroup") {
        const response = edit
          ? await updateAgeGroup({
              id: item._id,
              title: trimmedTitle,
            }).unwrap()
          : await createAgeGroup({
              title: trimmedTitle,
            }).unwrap();
        showToast({
          message: response?.message || "Operation successful",
          type: "success",
        });
      }

      if (type === "courseLevel") {
        const response = edit
          ? await updateCourseLevel({
              id: item._id,
              title: trimmedTitle,
            }).unwrap()
          : await createCourseLevel({
              title: trimmedTitle,
            }).unwrap();
        showToast({
          message: response?.message || "Operation successful",
          type: "success",
        });
      }

      setTitle("");
      onClose();
    } catch (err) {
      console.error(err);

      setApiError(
        err?.data?.message ||
          `Something went wrong while ${
            edit ? "updating" : "creating"
          } the ${currentConfig.label.toLowerCase()}.`,
      );
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;

    setTitle("");
    setApiError("");
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-6 sm:p-10">
      <div
        ref={modalRef}
        className="w-full max-w-[500px] overflow-hidden rounded-lg bg-white"
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-4 border-b border-[#E5E7EB] p-4 sm:p-6">
          <h3 className="text-lg font-semibold leading-7 text-[#111827] sm:text-xl">
            {edit
              ? `Edit ${currentConfig.label}`
              : `Add New ${currentConfig.label}`}
          </h3>

          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="rounded-md p-1 text-[#9CA3AF] transition hover:bg-black/5 disabled:cursor-not-allowed"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit}>
          <div className="px-4 pb-6 pt-5 sm:px-6 sm:pb-7">
            <div>
              <div className="flex items-center gap-1">
                <label
                  htmlFor="subCategoryTitle"
                  className="mb-2 text-sm font-medium leading-5 text-[#374151]"
                >
                  {currentConfig.label}
                </label>

                <span className="text-red-500">*</span>
              </div>

              <input
                id="categoryOptionTitle"
                type="text"
                value={title}
                onChange={(event) => {
                  setTitle(event.target.value);

                  if (apiError) {
                    setApiError("");
                  }
                }}
                placeholder={currentConfig.placeholder}
                disabled={isSubmitting}
                className="h-[38px] w-full rounded-lg border border-[#D1D5DB] px-3 text-sm outline-none placeholder:text-[#9CA3AF] focus:border-[#923D8C] disabled:bg-gray-50"
              />
            </div>

            {apiError && (
              <div className="mt-4 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
                <AlertCircle size={16} />
                <span>{apiError}</span>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-[#E5E7EB] px-4 py-4 sm:px-6">
            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-end">
              <button
                type="button"
                onClick={handleClose}
                disabled={isSubmitting}
                className="rounded-lg bg-[#F3F4F6] px-4 py-2 leading-6 text-[#374151] disabled:opacity-50"
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={isSubmitting || !title.trim()}
                className="rounded-lg bg-[#923D8C] px-6 py-2 leading-6 text-white transition hover:bg-[#7F3479] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isSubmitting
                  ? edit
                    ? "Updating..."
                    : "Creating..."
                  : edit
                    ? `Update ${currentConfig.label}`
                    : `Create ${currentConfig.label}`}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddCategoryOptionModal;
